import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import type { TeamQuery } from 'generated/resources/graphql'
import moment from 'moment'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'

import membershipsCollection from 'modules/teams/store/collections/memberships'
import type { ITeam } from 'modules/teams/store/models/Team'

import Settings from './_Settings'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface Props {
  membership: TeamMembership
  team: ITeam
  readonly?: boolean
  hasDevJobCatalogTree?: boolean
}

const Item = ({ membership, team, readonly, hasDevJobCatalogTree }: Props) => {
  const { navigation } = useNavigation()
  const employee = membership.employee
  const referenceContract = employee.referenceContract
  const jobCatalogLevel = referenceContract?.jobCatalogLevel

  // Get membership model for actions
  const membershipModel = membershipsCollection.find({
    employee_id: employee.id,
    team_id: team.id,
  })

  const hiredOn = referenceContract?.startsOn ? moment(referenceContract.startsOn).fromNow() : '-'

  const assertCellEllipsis = (text: string, maxCharacters = 8): boolean => {
    return text.length > maxCharacters
  }

  const fitText = (text: string) => {
    return (
      <Text.Normal
        weight="medium"
        whiteSpace="nowrap"
        ellipsis={assertCellEllipsis(text || '', 14)}
      >
        {text}
      </Text.Normal>
    )
  }

  const getLevelName = () => {
    if (hasDevJobCatalogTree) {
      return undefined
    }

    return jobCatalogLevel?.isDefault ? '' : jobCatalogLevel?.name
  }

  const managerFullName = employee.manager
    ? `${employee.manager.firstName} ${employee.manager.lastName}`
    : ''

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box flexDirection="row" gap="s24" alignItems="center">
          <UserAvatar
            size="s40"
            user={{
              fullName: `${employee.firstName} ${employee.lastName}`,
              avatar: employee?.avatar?.url ?? '',
            }}
          />
          {fitText(
            getEmployeeDisplayName({
              fullName: `${employee.firstName} ${employee.lastName}`,
              lastName: employee.lastName,
              preferredName: employee.preferredName ?? undefined,
            })
          )}
        </Box>
      </Table.Cell>
      {!hasDevJobCatalogTree && <Table.Cell>{fitText(getLevelName() || '')}</Table.Cell>}
      <Table.Cell>
        <Text.Normal weight="normal">{hiredOn}</Text.Normal>
      </Table.Cell>
      <Table.Cell colSpan={2}>{fitText(managerFullName)}</Table.Cell>
      <Table.Cell>
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          {membershipModel && <Settings membership={membershipModel} readonly={readonly} />}
          <Button.Router
            to={navigation.employees.id.fullPath({ id: employee.id })}
            type="secondary"
            iconName="arrow-right"
          />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default Item
